import { useState } from 'react';
import { Button } from '../../../shared/components/ui/Button/Button';
import { Input } from '../../../shared/components/ui/Input/Input';
import { useVerifyToken } from '../hooks/UseVerifyToken';
import { validatePassword, getPasswordRequirements } from '../../../shared/utils/passwordValidation';
import { useTheme } from '../../../shared/context/ThemeContext';
import { useBrand } from '../../../shared/context/BrandContext';

interface VerifyTokenFormProps {
  token: string;
}

export default function VerifyTokenForm({ token }: VerifyTokenFormProps) {
  const { effectiveTheme } = useTheme();
  const { brand, brandId, getLogo, getPublicAsset } = useBrand();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showRequirements, setShowRequirements] = useState(false);
  const { verifyToken, isLoading, error, success } = useVerifyToken();

  const passwordRequirements = getPasswordRequirements(password);

  // Para la marca ALMA usamos el isologo servido desde `public/alma/iso-light.png`
  // (getPublicAsset reescribe el path a la subcarpeta de la marca). El resto de
  // marcas siguen usando su logo `full` por tema.
  const resolveLogo = (theme: 'light' | 'dark') =>
    brandId === 'ALMA_MEDICAL'
      ? getPublicAsset('/iso-light.png')
      : getLogo('full', theme);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    // Validar que los campos no estén vacíos
    if (!password.trim()) {
      setPasswordError('La contraseña es requerida');
      return;
    }

    if (!confirmPassword.trim()) {
      setPasswordError('Debes confirmar la contraseña');
      return;
    }

    // Validar que las contraseñas coincidan
    if (password !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }

    // Validar fortaleza de la contraseña
    const validation = validatePassword(password);
    if (!validation.isValid) {
      setPasswordError(validation.errors[0]);
      return;
    }

    verifyToken(token, password);
  };

  if (success) {
    return (
      <div className="verify-token-form">
        <img src={resolveLogo('light')} alt={brand.displayName} className="verify-token-logo" />
        <div className="success-message">
          <h2>¡Cuenta verificada exitosamente!</h2>
          <p>Tu cuenta ha sido verificada y tu contraseña ha sido establecida.</p>
          <p>Redirigiendo al login...</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="verify-token-form">
      <img src={resolveLogo(effectiveTheme)} alt={brand.displayName} className="verify-token-logo" />
      
      <div className="verify-token-header">
        <h1>Verifica tu cuenta</h1>
        <p>Establece una contraseña para tu cuenta</p>
      </div>

      {error && <div className="error">{error}</div>}
      {passwordError && <div className="error">{passwordError}</div>}
      
      <Input
        type="password"
        placeholder="Contraseña"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onFocus={() => setShowRequirements(true)}
      />
      
      {showRequirements && password && (
        <div className="password-requirements">
          <p className="requirements-title">La contraseña debe contener:</p>
          <ul className="requirements-list">
            <li className={passwordRequirements.minLength ? 'valid' : 'invalid'}>
              {passwordRequirements.minLength ? '✓' : '○'} Mínimo 8 caracteres
            </li>
            <li className={passwordRequirements.hasUpperCase ? 'valid' : 'invalid'}>
              {passwordRequirements.hasUpperCase ? '✓' : '○'} Una letra mayúscula
            </li>
            <li className={passwordRequirements.hasLowerCase ? 'valid' : 'invalid'}>
              {passwordRequirements.hasLowerCase ? '✓' : '○'} Una letra minúscula
            </li>
            <li className={passwordRequirements.hasNumber ? 'valid' : 'invalid'}>
              {passwordRequirements.hasNumber ? '✓' : '○'} Un número
            </li>
            <li className={passwordRequirements.hasSpecialChar ? 'valid' : 'invalid'}>
              {passwordRequirements.hasSpecialChar ? '✓' : '○'} Un carácter especial (!@#$%^&*...)
            </li>
          </ul>
        </div>
      )}
      
      <Input
        type="password"
        placeholder="Confirmar contraseña"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />
      
      <Button type="submit" disabled={isLoading}>
        {isLoading ? 'Verificando...' : 'Verificar cuenta'}
      </Button>
    </form>
  );
}
